import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import $ from 'jquery';

import IziToast from '../IziToast';
import Master from '../Master';
import TextInput from '../TextInput';
import CheckBox from '../CheckBox';
import Platforms from '../../data/Platforms';
import BankCodes from '../../data/BankCodes';
import Select from '../Select';
import Button from '../Buttons';
import PropertyTable from './PropertyTable';
import Currency from '../../data/Currency';
import FilePreview from '../FileUploader/FilePreview';

// Platforms that use their own bank routing (no paybill / landlord bank details needed on setup)
const BANK_PLATFORMS = ['co-op', 'ncba'];

const NewProperty = ({ user }) => {
  const userToken = user ? user.token : {};
  const requestConfig = {
    headers: {
      Authorization: `Bearer ${userToken}`,
      'Content-Type': 'application/json',
    },
  };
  const history = useHistory();

  // refs
  const landlordRef = React.useRef(null);
  const bankNameRef = React.useRef(null);
  const bankCodeRef = React.useRef(null);

  // Core property fields
  const [propertyName, setPropertyName] = useState('');
  const [propertyCode, setPropertyCode] = useState('');
  const [description, setDescription] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [propertyUnits, setPropertyUnits] = useState('');
  const [paymentPlatform, setPaymentPlatform] = useState(Platforms[0].id);
  const [currency, setCurrency] = useState(Currency[0].id);
  const [landLords, setLandLords] = useState([]);
  const [landLordId, setLandLordId] = useState('');
  const [currentFile, setCurrentFile] = useState(null);
  // hasAttemptedSubmit drives the red "error" highlighting on empty required
  // fields — once true it stays true for the rest of this form session, so
  // fields the user hasn't fixed yet keep showing as invalid. isSubmitting
  // is a SEPARATE flag scoped strictly to "the save request is in flight" —
  // kept apart from hasAttemptedSubmit so it can never get stuck true (it's
  // only ever set right before the API call and is always reset once that
  // call settles, success or failure).
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Mpesa / MoMo fields
  const [accountNumber, setAccountNumber] = useState('');
  const [tenantPaymentEmail, setTenantPaymentEmail] = useState('');
  const [tenantPaymentPhone, setTenantPaymentPhone] = useState('');

  // Landlord bank (pesalink) fields — shown for mpesa / momo
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankCode, setBankCode] = useState('');
  const [bankTelephoneNumber, setBankTelephoneNumber] = useState('');
  const [isEquityAccount, setIsEquityAccount] = useState(false);

  // Bank integration fields — shown for co-op / ncba
  const [coopAccount, setCoopAccount] = useState('');
  const [coopNarrationId, setCoopNarrationId] = useState('');
  const [ncbaShortcode, setNcbaShortcode] = useState('');

  // Suspense notification settings — shown for co-op / ncba
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [notifWhatsapp, setNotifWhatsapp] = useState(false);
  const [notifEmail, setNotifEmail] = useState(false);
  const [notifWhatsappNumber, setNotifWhatsappNumber] = useState('');
  const [notifEmailAddress, setNotifEmailAddress] = useState('');

  // Derived helpers
  const isBankPlatform = BANK_PLATFORMS.includes(paymentPlatform);
  const isCoop = paymentPlatform === 'co-op';
  const isNcba = paymentPlatform === 'ncba';

  useEffect(() => {
    if (landLords.length > 0) setLandLordId(landLords[0].id);
  }, [landLords]);

  useEffect(() => {
    (async () => {
      try {
        let url;
        switch (user.type) {
          case 'admin':
            url = `${process.env.REACT_APP_API_URL}users?type=landlord&status=active`;
            break;
          case 'landlord':
          case 'agent':
            url = `${process.env.REACT_APP_API_URL}user`;
            break;
          default:
            break;
        }
        const { data: { response } } = await axios.get(url, requestConfig);
        if (user.type === 'admin') {
          const urlAgent = `${process.env.REACT_APP_API_URL}users?type=agent&status=active`;
          const { data: { response: agents } } = await axios.get(urlAgent, requestConfig);
          setLandLords(agents.concat(response));
        }
        if (user.type === 'landlord' || user.type === 'agent') {
          setLandLords([response]);
        }
        $(landlordRef.current).select2().on('change', (e) => setLandLordId(e.val));
        $(bankNameRef.current).select2().on('change', (e) => setBankAccountName(e.val));
        $(bankCodeRef.current).select2().on('change', (e) => setBankCode(e.val));
      } catch (error) { /* ignore */ }
    })();
  }, []);

  // ─── Validation ───────────────────────────────────────────────────────────
  const validateAndSave = async () => {
    setHasAttemptedSubmit(true);

    const baseValid = propertyName && propertyCode && description
      && propertyUnits && locationAddress && currency && landLordId;

    let platformValid = false;
    if (isCoop) {
      platformValid = !!coopAccount;
    } else if (isNcba) {
      platformValid = !!ncbaShortcode;
    } else {
      // mpesa / momo
      platformValid = accountNumber
        && bankAccountName && bankAccountNumber && bankCode && bankTelephoneNumber
        && (paymentPlatform === 'momo' ? tenantPaymentPhone && tenantPaymentEmail : true);
    }

    if (!baseValid || !platformValid) {
      IziToast('Please fill in all required fields');
      return;
    }

    // ─── Build payload ─────────────────────────────────────────────────────
    const otherDetails = { currency };

    let tenantPaymentInfo;
    let landlordPaymentInfo;

    if (isCoop) {
      otherDetails.bank_integrations = { 'co-op': coopAccount.trim() };
      if (coopNarrationId.trim()) {
        otherDetails.bank_integrations['co-op_narration'] = coopNarrationId.trim();
      }
      // Suspense notifications
      if (notifEnabled) {
        const channels = [...(notifWhatsapp ? ['whatsapp'] : []), ...(notifEmail ? ['email'] : [])];
        otherDetails.suspense_notifications = {
          enabled: true,
          channels,
          whatsapp_number: notifWhatsapp ? notifWhatsappNumber.trim() : null,
          email: notifEmail ? notifEmailAddress.trim() : null,
        };
      }
      tenantPaymentInfo = [{
        payment_info: { account: coopAccount.trim(), platform: 'co-op' },
        payment_type: 'rent',
      }];
      landlordPaymentInfo = []; // Landlord payout handled outside system for bank platforms
    } else if (isNcba) {
      otherDetails.bank_integrations = { ncba: ncbaShortcode.trim() };
      // Suspense notifications
      if (notifEnabled) {
        const channels = [...(notifWhatsapp ? ['whatsapp'] : []), ...(notifEmail ? ['email'] : [])];
        otherDetails.suspense_notifications = {
          enabled: true,
          channels,
          whatsapp_number: notifWhatsapp ? notifWhatsappNumber.trim() : null,
          email: notifEmail ? notifEmailAddress.trim() : null,
        };
      }
      tenantPaymentInfo = [{
        payment_info: { account: ncbaShortcode.trim(), platform: 'ncba' },
        payment_type: 'rent',
      }];
      landlordPaymentInfo = [];
    } else {
      tenantPaymentInfo = [{
        payment_info: paymentPlatform === 'momo'
          ? { account: accountNumber, platform: paymentPlatform, phone: tenantPaymentPhone, email: tenantPaymentEmail }
          : { account: accountNumber, platform: paymentPlatform },
        payment_type: 'rent',
      }];
      landlordPaymentInfo = [{
        payment_info: {
          account: bankAccountNumber,
          platform: 'pesalink',
          bank_name: bankAccountName,
          code: bankCode,
          phone: bankTelephoneNumber,
          is_equity_account: isEquityAccount,
        },
        payment_type: 'rent',
      }];
    }

    const formData = new FormData();
    formData.append('user_id', landLordId);
    formData.append('name', propertyName);
    formData.append('property_code', propertyCode);
    formData.append('description', description);
    formData.append('address', locationAddress);
    formData.append('units', propertyUnits);
    formData.append('other_details', JSON.stringify(otherDetails));
    formData.append('tenant_payment_details', JSON.stringify(tenantPaymentInfo));
    formData.append('landlord_payment_details', JSON.stringify(landlordPaymentInfo));
    if (currentFile) formData.append(currentFile.name, currentFile, currentFile.name);

    const formConfig = {
      headers: { Authorization: `Bearer ${userToken}`, 'Content-Type': 'multipart/form-data' },
    };

    IziToast('Saving...');
    setIsSubmitting(true);
    try {
      const { data: { response } } = await axios.post(
        `${process.env.REACT_APP_API_URL}properties`,
        formData,
        formConfig,
      );
      if (response.propertySaved.id) {
        IziToast('Property successfully saved');
        resetFields();
        setIsLoading((prev) => !prev);
      }
    } catch (error) {
      if (error.response && error.response.data) {
        IziToast(error.response.data.error);
      } else {
        IziToast(error.message || 'An error occurred');
      }
    }
    setIsSubmitting(false);
  };

  const resetFields = () => {
    setPropertyName(''); setPropertyCode(''); setDescription('');
    setLocationAddress(''); setPropertyUnits('');
    setPaymentPlatform(Platforms[0].id); setCurrency(Currency[0].id);
    setLandLordId(''); setCurrentFile(null);
    setAccountNumber(''); setTenantPaymentEmail(''); setTenantPaymentPhone('');
    setBankAccountName(''); setBankAccountNumber('');
    setBankCode(''); setBankTelephoneNumber(''); setIsEquityAccount(false);
    setCoopAccount(''); setCoopNarrationId(''); setNcbaShortcode('');
    setNotifEnabled(false); setNotifWhatsapp(false); setNotifEmail(false);
    setNotifWhatsappNumber(''); setNotifEmailAddress('');
  };

  const onFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png'];
    if (validTypes.includes(file.type)) {
      setCurrentFile(file);
    } else {
      IziToast('Only .jpeg and .png files allowed');
    }
  };

  return (
    <Master redirectLink="newproperty" title="Property Management" source="New Property">
      <div className="col-xs-3 bm-responsive-div">
        <FilePreview
          currentFile={currentFile}
          onChange={onFileChange}
          inputId="imgProperty"
          onFileRemove={() => setCurrentFile(null)}
          columnWidth={12}
        />
      </div>

      <div className="col-xs-9 bm-responsive-div">

        {/* ── Landlord / Property basics ──────────────────────────────── */}
        <div className="row">
          <Select
            caption="Landlord/Agent"
            value={landLordId}
            onChange={(e) => setLandLordId(e.target.value)}
            columnWidth={6}
            className={hasAttemptedSubmit && !landLordId ? 'error' : ''}
            inputRef={landlordRef}
          >
            {user.type === 'admin' && <option value="">Select property landlord/agent</option>}
            {landLords.map((item) => (
              <option value={item.id} key={item.id}>
                {`${item.email} | ${item.first_name} ${item.last_name}`}
              </option>
            ))}
          </Select>
          <TextInput
            type="text"
            placeholder="Enter property name"
            caption="Property Name"
            onChange={(e) => setPropertyName(e.target.value)}
            value={propertyName}
            columnWidth={6}
            className={hasAttemptedSubmit && !propertyName ? 'error' : ''}
          />
        </div>

        <div className="row">
          <TextInput
            type="text"
            placeholder="Enter property code"
            caption="Property Code"
            onChange={(e) => setPropertyCode(e.target.value)}
            value={propertyCode}
            columnWidth={6}
            className={hasAttemptedSubmit && !propertyCode ? 'error' : ''}
          />
          <TextInput
            type="text"
            placeholder="Enter units"
            caption="Units"
            onChange={(e) => {
              if (e.target.value === '' || /^[0-9\b]+$/.test(e.target.value)) {
                setPropertyUnits(e.target.value);
              }
            }}
            value={propertyUnits}
            columnWidth={6}
            className={hasAttemptedSubmit && !propertyUnits ? 'error' : ''}
          />
        </div>

        <div className="row">
          <TextInput
            type="text"
            placeholder="Enter property description"
            caption="Property Description"
            onChange={(e) => setDescription(e.target.value)}
            value={description}
            columnWidth={12}
            className={hasAttemptedSubmit && !description ? 'error' : ''}
          />
        </div>

        <div className="row">
          <TextInput
            type="text"
            placeholder="Enter area, town"
            caption="Area, Town"
            onChange={(e) => setLocationAddress(e.target.value)}
            value={locationAddress}
            columnWidth={6}
            className={hasAttemptedSubmit && !locationAddress ? 'error' : ''}
          />
        </div>

        <br />

        {/* ── Payment Platform + Currency ─────────────────────────────── */}
        <div className="row">
          <Select
            caption="Payment Platform"
            value={paymentPlatform}
            onChange={(e) => setPaymentPlatform(e.target.value)}
            columnWidth={6}
            className={hasAttemptedSubmit && !paymentPlatform ? 'error' : ''}
          >
            {Platforms.map((item) => (
              <option value={item.id} key={item.id}>{item.name}</option>
            ))}
          </Select>

          <Select
            caption="Currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            columnWidth={6}
            className={hasAttemptedSubmit && !currency ? 'error' : ''}
          >
            {Currency.map((item) => {
              // Bank platforms always use KES
              if (isBankPlatform) {
                return item.id === 'KES'
                  ? <option value={item.id} key={item.id}>{item.name}</option>
                  : null;
              }
              if (paymentPlatform === 'mpesa_paybill') {
                return item.id === 'KES'
                  ? <option value={item.id} key={item.id}>{item.name}</option>
                  : null;
              }
              if (paymentPlatform === 'momo') {
                return item.id !== 'KES'
                  ? <option value={item.id} key={item.id}>{item.name}</option>
                  : null;
              }
              return null;
            })}
          </Select>
        </div>

        {/* ── CO-OP: only show Co-op account number ──────────────────── */}
        {isCoop && (
          <div className="row">
            <div className="col-md-12">
              <div
                className="alert"
                style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '10px 14px', marginBottom: 10 }}
              >
                <i className="fa fa-info-circle" style={{ marginRight: 6, color: '#f39c12' }} />
                Co-op Bank payments are automatically routed to this property using the account number below.
                Tenants pay using their registered phone number — no property code required.
              </div>
            </div>
            <TextInput
              type="text"
              placeholder="e.g. 01143650439100"
              caption="Co-op Bank Account Number"
              onChange={(e) => setCoopAccount(e.target.value)}
              value={coopAccount}
              columnWidth={6}
              className={hasAttemptedSubmit && !coopAccount ? 'error' : ''}
            />
            <TextInput
              type="text"
              placeholder="e.g. 1834987"
              caption="Co-op Narration ID (optional)"
              onChange={(e) => setCoopNarrationId(e.target.value)}
              value={coopNarrationId}
              columnWidth={6}
            />
            <div className="col-md-12">
              <small style={{ color: '#777', fontStyle: 'italic' }}>
                The fixed prefix that appears in your Co-op narration before the unit number
                (e.g. <code>1834987</code> from <code>1834987#G4</code>). When set, payments are
                routed by narration first — the suffix (<code>G4</code>) maps to the tenant&apos;s unit number.
              </small>
            </div>
          </div>
        )}

        {/* ── NCBA: only show NCBA shortcode ─────────────────────────── */}
        {isNcba && (
          <div className="row">
            <div className="col-md-12">
              <div
                className="alert"
                style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 4, padding: '10px 14px', marginBottom: 10 }}
              >
                <i className="fa fa-info-circle" style={{ marginRight: 6, color: '#f39c12' }} />
                NCBA payments are routed using the Property Reference (BillRefNumber)
                below. Tenants enter their unit number in the Narrative when paying —
                that value must match a unit number in this property.
              </div>
            </div>
            <TextInput
              type="text"
              placeholder="e.g. 132228"
              caption="NCBA Property Reference (BillRefNumber)"
              onChange={(e) => setNcbaShortcode(e.target.value)}
              value={ncbaShortcode}
              columnWidth={6}
              className={hasAttemptedSubmit && !ncbaShortcode ? 'error' : ''}
            />
          </div>
        )}

        {/* ── Suspense notification settings (co-op / ncba only) ─────── */}
        {isBankPlatform && (
          <div className="row" style={{ marginTop: 8 }}>
            <div className="col-md-12">
              <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: '16px 20px', background: '#fafafa' }}>
                <label className="control-label" style={{ fontSize: 14, marginBottom: 10, display: 'block' }}>
                  <i className="fa fa-bell" style={{ marginRight: 6, color: '#c0392b' }} />
                  Suspense Payment Notifications
                  <small style={{ fontWeight: 'normal', marginLeft: 8, color: '#777' }}>
                    — notify when a payment cannot be automatically reconciled
                  </small>
                </label>

                {/* Enable toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <span style={{ fontSize: 13, color: '#555' }}>Enable notifications</span>
                  <div
                    onClick={() => setNotifEnabled((v) => !v)}
                    style={{
                      cursor: 'pointer',
                      width: 48, height: 26, borderRadius: 13,
                      background: notifEnabled ? '#c0392b' : '#ccc',
                      position: 'relative', transition: 'background 0.2s',
                    }}
                  >
                    <div style={{
                      position: 'absolute', top: 3,
                      left: notifEnabled ? 25 : 3,
                      width: 20, height: 20, borderRadius: '50%',
                      background: '#fff', transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,.3)',
                    }} />
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 'bold', color: notifEnabled ? '#c0392b' : '#999' }}>
                    {notifEnabled ? 'ON' : 'OFF'}
                  </span>
                </div>

                {notifEnabled && (
                  <>
                    {/* Channel checkboxes */}
                    <div style={{ marginBottom: 14 }}>
                      <label className="control-label" style={{ fontSize: 13 }}>Notification Channel</label>
                      <div style={{ display: 'flex', gap: 24, marginTop: 6 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={notifWhatsapp}
                            onChange={(e) => setNotifWhatsapp(e.target.checked)}
                          />
                          <i className="fa fa-whatsapp" style={{ color: '#25D366', fontSize: 16 }} />
                          WhatsApp
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={notifEmail}
                            onChange={(e) => setNotifEmail(e.target.checked)}
                          />
                          <i className="fa fa-envelope" style={{ color: '#3498db', fontSize: 14 }} />
                          Email
                        </label>
                      </div>
                    </div>

                    {/* WhatsApp number */}
                    {notifWhatsapp && (
                      <div className="row">
                        <TextInput
                          type="text"
                          placeholder="e.g. +254712345678 (include country code)"
                          caption="WhatsApp Number"
                          onChange={(e) => setNotifWhatsappNumber(e.target.value)}
                          value={notifWhatsappNumber}
                          columnWidth={6}
                        />
                      </div>
                    )}

                    {/* Email address */}
                    {notifEmail && (
                      <div className="row">
                        <TextInput
                          type="email"
                          placeholder="e.g. landlord@example.com"
                          caption="Notification Email"
                          onChange={(e) => setNotifEmailAddress(e.target.value)}
                          value={notifEmailAddress}
                          columnWidth={6}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <br />

        {/* ── MPESA / MOMO: paybill + landlord bank details ──────────── */}
        {!isBankPlatform && (
          <>
            <div className="row">
              <TextInput
                type="text"
                placeholder="Enter paybill number"
                caption="Paybill Number"
                onChange={(e) => {
                  if (e.target.value === '' || /^[0-9\b]+$/.test(e.target.value)) {
                    setAccountNumber(e.target.value);
                  }
                }}
                value={accountNumber}
                columnWidth={6}
                className={hasAttemptedSubmit && !accountNumber ? 'error' : ''}
              />
              {paymentPlatform === 'momo' && (
                <TextInput
                  type="email"
                  placeholder="Enter email"
                  caption="Linked Email Address"
                  onChange={(e) => setTenantPaymentEmail(e.target.value)}
                  value={tenantPaymentEmail}
                  columnWidth={6}
                  className={hasAttemptedSubmit && !tenantPaymentEmail ? 'error' : ''}
                />
              )}
              {paymentPlatform === 'momo' && (
                <TextInput
                  type="text"
                  placeholder="Enter phone number"
                  caption="Linked Telephone Number"
                  onChange={(e) => setTenantPaymentPhone(e.target.value)}
                  value={tenantPaymentPhone}
                  columnWidth={6}
                  className={hasAttemptedSubmit && !tenantPaymentPhone ? 'error' : ''}
                />
              )}
            </div>

            <br />

            <div className="row">
              <Select
                caption="Enter bank account name"
                value={bankAccountName}
                onChange={(e) => setBankAccountName(e.target.value)}
                columnWidth={6}
                className={hasAttemptedSubmit && !bankAccountName ? 'error' : ''}
                inputRef={bankNameRef}
              >
                <option value="">Select bank account name</option>
                {BankCodes.map((item) => (
                  <option value={item.name} key={item.name}>{item.name}</option>
                ))}
              </Select>
              <Select
                caption="Bank Code"
                value={bankCode}
                onChange={(e) => setBankCode(e.target.value)}
                columnWidth={6}
                className={hasAttemptedSubmit && !bankCode ? 'error' : ''}
                inputRef={bankCodeRef}
              >
                <option value="">Select bank code</option>
                {BankCodes.map((item) => (
                  <option value={item.code} key={item.code}>
                    {`${item.code} - ${item.name}`}
                  </option>
                ))}
              </Select>
              <TextInput
                type="text"
                placeholder="Enter bank account number"
                caption="Bank Account Number"
                onChange={(e) => {
                  if (e.target.value === '' || /^[0-9\b]+$/.test(e.target.value)) {
                    setBankAccountNumber(e.target.value);
                  }
                }}
                value={bankAccountNumber}
                columnWidth={6}
                className={hasAttemptedSubmit && !bankAccountNumber ? 'error' : ''}
              />
              <TextInput
                type="text"
                placeholder="Enter telephone number linked to account"
                caption="Linked Telephone Number"
                onChange={(e) => setBankTelephoneNumber(e.target.value)}
                value={bankTelephoneNumber}
                columnWidth={6}
                className={hasAttemptedSubmit && !bankTelephoneNumber ? 'error' : ''}
              />
              <div className="col-md-6">
                <label className="control-label" htmlFor="">
                  Is it an Equity Bank account?
                  <div className="row" style={{ marginLeft: '3px', paddingTop: '10px' }}>
                    <CheckBox
                      caption="YES"
                      checked={isEquityAccount}
                      onClick={() => setIsEquityAccount(true)}
                    />
                    <CheckBox
                      caption="NO"
                      checked={!isEquityAccount}
                      onClick={() => setIsEquityAccount(false)}
                    />
                  </div>
                </label>
              </div>
            </div>
          </>
        )}

        <br />

        {/* ── Save / Back buttons ─────────────────────────────────────── */}
        <div className="row">
          <div className="col-md-12">
            <div className="right form-group">
              <Button
                caption="<< Back To Properties"
                cssClass="btn-success"
                onClick={() => history.push('/property')}
                type="button"
                isBlack
              />
              <Button
                caption="Save Property"
                cssClass="btn-success"
                onClick={validateAndSave}
                type="button"
                isBlack={false}
              />
            </div>
          </div>
        </div>
      </div>

      <PropertyTable reLoadTable={isLoading} userToken={userToken} userInfo={user} />
    </Master>
  );
};

const mapStateToProps = (state) => state;

NewProperty.propTypes = {
  user: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
};

NewProperty.defaultProps = {
  user: {},
};

export default connect(mapStateToProps)(NewProperty);
